import { useEffect, useRef, type MutableRefObject } from "react";
import { ExportButton } from "@/components/ExportButton";
import type { Exporter, Identifier, RaRecord } from "react-admin";
import {
  Datagrid,
  List,
  TextField,
  FunctionField,
  SimpleList,
  useListContext,
  usePermissions,
  Pagination,
  TopToolbar,
} from "react-admin";
import DownloadIcon from "@mui/icons-material/Download";
import { formatListDate } from "@/utils/date-format";
import DisplayDateField from "@/components/DisplayDateField";
import { Box, IconButton, Link, useMediaQuery } from "@mui/material";
import { exportToExcel } from "@/utils/export-to-excel";
import { isRentTallyConfigReady } from "@/types/tally-config";
import type { ExcelColumn } from "@/types/excel";
import { formatINR } from "@/utils/app-formatters";
import { hasPermission } from "@/utils/permission-utils";
import TallyExportButton from "@/components/TallyExportButton";
import StackedDatagridCell from "@/components/StackedDatagridCell";
import RentBookingReceiptFilterBar from "./RentBookingReceiptFilters";
import RentBookingReceiptPaymentSummary from "./RentBookingReceiptPaymentSummary";
import {
  type RentBookingReceiptRow,
  DEFAULT_RECEIPT_MODE,
  DEFAULT_RECEIPT_TYPE,
  bookingId,
  bookingNo,
  createdByLabel,
  navigateToBookingShow,
  getFilterFromURL,
  hallNamesLabel,
  openReceiptPrint,
  receiptCategoryLabel,
  receiptTypeLabel,
  rentExportFilenameSuffix,
} from "./rent-booking-receipt-utils";

const ReceiptDownloadAction = ({
  recordId,
  stopPropagation = false,
}: {
  recordId: string | number;
  stopPropagation?: boolean;
}) => (
  <IconButton
    size="small"
    color="primary"
    aria-label="Download receipt"
    onClick={(event) => {
      if (stopPropagation) {
        event.stopPropagation();
        event.preventDefault();
      }
      openReceiptPrint(recordId);
    }}
  >
    <DownloadIcon fontSize="small" />
  </IconButton>
);

const simpleListSx = {
  "& .MuiListItemButton-root": { alignItems: "center" },
  "& .MuiListItemIcon-root": { minWidth: "auto", justifyContent: "center", alignItems: "center" },
} as const;

const BookingNoCell = ({
  record,
  canViewBookings,
}: {
  record: RentBookingReceiptRow;
  canViewBookings: boolean;
}) => {
  const id = bookingId(record);
  const label = bookingNo(record);
  if (!id) return <>{label}</>;
  if (canViewBookings) {
    return (
      <Link
        component="button"
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          navigateToBookingShow(id);
        }}
        sx={{
          fontWeight: 600,
          border: 0,
          background: "none",
          padding: 0,
          font: "inherit",
          color: "primary.main",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        {label}
      </Link>
    );
  }
  return <>{label}</>;
};

const ReceiptListBody = ({
  isSmall,
  canViewBookings,
}: {
  isSmall: boolean;
  canViewBookings: boolean;
}) => {
  const { filterValues } = useListContext();
  const isDeposit = !filterValues?.type || filterValues.type === "DEPOSIT";

  if (isSmall) {
    return (
      <SimpleList
        primaryText={(record) => String(record.receiptNo ?? "—")}
        secondaryText={(record) => {
          const row = record as RentBookingReceiptRow;
          const dateLabel = row.date ? formatListDate(row.date as string) : "—";
          return (
            <>
              {row.organiser ?? "—"}
              {row.organiserIts ? ` · ITS ${row.organiserIts}` : ""}
              <br />
              {receiptCategoryLabel(row)} · Booking {bookingNo(row)} · {hallNamesLabel(row)} ·{" "}
              {dateLabel} · {formatINR(row.amount)}
              {isDeposit && row.refundAmount ? ` · Refund ${formatINR(row.refundAmount)}` : ""}
            </>
          );
        }}
        tertiaryText={(record) => {
          const details = String((record as RentBookingReceiptRow).details ?? "").trim();
          return details || "—";
        }}
        linkType="edit"
        rightIcon={(record) => <ReceiptDownloadAction recordId={record.id} stopPropagation />}
        sx={simpleListSx}
        rowSx={() => ({ borderBottom: 1, borderBottomColor: "divider" })}
      />
    );
  }

  return (
    <Datagrid rowClick="edit" bulkActionButtons={false}>
      <TextField source="receiptNo" />
      <FunctionField
        label="Category"
        sortBy="type"
        render={(record: RentBookingReceiptRow) => (
          <StackedDatagridCell
            primary={receiptTypeLabel(record.type)}
            secondary={String(record.mode ?? "—")}
          />
        )}
      />
      <FunctionField
        label="Booking"
        sortBy="bookingId"
        render={(record: RentBookingReceiptRow) => (
          <StackedDatagridCell
            primary={<BookingNoCell record={record} canViewBookings={canViewBookings} />}
            secondary={hallNamesLabel(record)}
          />
        )}
      />
      <FunctionField
        label="Organiser"
        sortBy="organiser"
        render={(record: RentBookingReceiptRow) => (
          <StackedDatagridCell
            primary={record.organiser ?? "—"}
            secondary={record.organiserIts ? `ITS ${record.organiserIts}` : "—"}
          />
        )}
      />
      <FunctionField
        label="Payment"
        sortBy="amount"
        render={(record: RentBookingReceiptRow) => (
          <Box sx={{ textAlign: "right", fontWeight: 600 }}>{formatINR(record.amount)}</Box>
        )}
      />
      <DisplayDateField source="date" label="Date" emptyText="—" />
      {isDeposit ? (
        <FunctionField
          label="Refund"
          sortBy="refundAmount"
          render={(record: RentBookingReceiptRow) =>
            record.refundAmount ? formatINR(record.refundAmount) : "—"
          }
        />
      ) : null}
      <FunctionField
        label="Remarks"
        source="details"
        sortable={false}
        render={(record: RentBookingReceiptRow) => record.details || "—"}
      />
      <FunctionField
        label="Download"
        source="formNo"
        sortable={false}
        render={(record: RentBookingReceiptRow) => (
          <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
            <ReceiptDownloadAction recordId={record.id} stopPropagation />
          </Box>
        )}
      />
    </Datagrid>
  );
};

const listToolbarSx = {
  justifyContent: "flex-start",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 1,
  rowGap: 1,
  width: "100%",
  px: { xs: 1, sm: 1.5 },
  py: 0.75,
  minHeight: 48,
  "& .MuiButton-root": {
    minHeight: 32,
    py: 0.5,
    px: 1.25,
    fontSize: "0.8125rem",
  },
  "& .MuiIconButton-root": { width: 32, height: 32 },
} as const;

const listToolbarActionsSx = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 1,
  rowGap: 1,
  ml: "auto",
} as const;

const ExportFilterSync = ({
  typeRef,
  modeRef,
}: {
  typeRef: MutableRefObject<string>;
  modeRef: MutableRefObject<string>;
}) => {
  const { filterValues } = useListContext();
  useEffect(() => {
    typeRef.current = filterValues?.type ? String(filterValues.type) : "";
    modeRef.current = filterValues?.mode ? String(filterValues.mode) : "";
  }, [filterValues?.type, filterValues?.mode, typeRef, modeRef]);
  return null;
};

const RentListActions = () => {
  const { permissions } = usePermissions();
  const { filterValues } = useListContext();
  const canView = hasPermission(permissions, "bookingReceipts.view");
  const rentOnly = String(filterValues?.type ?? "").toUpperCase() === "RENT";

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
      <TopToolbar sx={listToolbarSx}>
        <Box sx={listToolbarActionsSx}>
          {canView ? <ExportButton size="small" /> : null}
          {canView ? (
            <TallyExportButton
              module="rent"
              filenamePrefix="rent-tally"
              isConfigReady={isRentTallyConfigReady}
              disabled={!rentOnly}
            />
          ) : null}
        </Box>
      </TopToolbar>
      <RentBookingReceiptFilterBar />
    </Box>
  );
};

const buildExportColumns = (isDeposit: boolean): ExcelColumn<RaRecord<Identifier>>[] => [
  { header: "Receipt No", field: "receiptNo", width: 15 },
  {
    header: "Booking No",
    field: (rec) => bookingNo(rec as RentBookingReceiptRow),
    width: 20,
  },
  { header: "ITS No.", field: "organiserIts", width: 12 },
  { header: "Organiser", field: "organiser", width: 25 },
  {
    header: "Date",
    field: "date",
    width: 15,
    formatter: (_rec, v) => (v ? formatListDate(String(v)) : ""),
  },
  {
    header: "Amount",
    field: "amount",
    width: 12,
    formatter: (_rec, v) => formatINR(v),
  },
  ...(isDeposit
    ? [
        {
          header: "Refund Amount",
          field: (rec: RaRecord<Identifier>) => (rec as RentBookingReceiptRow).refundAmount || 0,
          width: 15,
        },
      ]
    : []),
  { header: "Mode", field: "mode", width: 15 },
  { header: "Remarks", field: "details", width: 28 },
  {
    header: "Halls",
    field: (rec) => hallNamesLabel(rec),
    width: 28,
  },
  {
    header: "Type",
    field: (rec) => ((rec as RentBookingReceiptRow).type === "RENT" ? "CONT" : rec.type || ""),
    width: 15,
  },
  {
    header: "Created By",
    field: (rec) => createdByLabel(rec as RentBookingReceiptRow),
    width: 20,
  },
];

export const RentBookingReceiptList = () => {
  const { permissions } = usePermissions();
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("sm"), { noSsr: true });
  const exportTypeRef = useRef(DEFAULT_RECEIPT_TYPE);
  const exportModeRef = useRef(DEFAULT_RECEIPT_MODE);
  const canView = hasPermission(permissions, "bookingReceipts.view");
  const canViewBookings = hasPermission(permissions, "bookings.view");

  const exporter = (records: RaRecord[]) => {
    const isDeposit = !exportTypeRef.current || exportTypeRef.current === "DEPOSIT";
    const tabName = rentExportFilenameSuffix(exportTypeRef.current, exportModeRef.current);
    return exportToExcel(buildExportColumns(isDeposit), records, {
      filenamePrefix: `receipts-${tabName}`,
      sheetName: "Receipts",
    });
  };

  return (
    <List
      sort={{ field: "date", order: "DESC" }}
      filterDefaultValues={getFilterFromURL()}
      actions={<RentListActions />}
      exporter={canView ? (exporter as unknown as Exporter<RaRecord<Identifier>>) : false}
      pagination={<Pagination rowsPerPageOptions={[5, 10, 25, 50]} />}
      sx={{
        "& .RaList-actions": {
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          width: "100%",
        },
        "& .RaList-actions > .MuiToolbar-root": listToolbarSx,
      }}
    >
      <ExportFilterSync typeRef={exportTypeRef} modeRef={exportModeRef} />
      <RentBookingReceiptPaymentSummary />
      <ReceiptListBody isSmall={isSmall} canViewBookings={canViewBookings} />
    </List>
  );
};

export default RentBookingReceiptList;
